import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    Button,
    IconButton,
    Box,
    Typography,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper,
    Collapse,
    Autocomplete,
    Popper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from 'react-toastify';
import DropzoneArea from '../DropzoneArea';
import Constants from '../../../../assests/Constants/constants';

const BorderBoxButton = styled(Button)({
    boxSizing: 'border-box',
});

const StyledPopper = styled(Popper)(({ theme }) => ({
    zIndex: 9999,
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#FFF',
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.15)',
        minWidth: '200px',
    },
    '& .MuiAutocomplete-listbox': {
        padding: theme.spacing(1),
        maxHeight: '280px',
    },
    '& .MuiAutocomplete-option': {
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '0.875rem',
        margin: '2px 0',
        '&[aria-selected="true"]': {
            background: 'rgba(48, 79, 255, 0.12) !important',
            fontWeight: 600,
        },
        '&.Mui-focused': {
            background: 'rgba(249, 170, 42, 0.15) !important',
        },
        '&:hover': {
            background: 'rgba(249, 170, 42, 0.25) !important',
        },
    },
}));

const MoveinDatImportModal = ({
    open,
    onClose,
    caseId,
    token,
    onSuccess,
    previewMovinDat,
    commitMovinDat,
    t,
    direction = 'rtl',
    sortCodeOptions = [],
    accountCardOptions = [],
    fetchEnums,
    createMissingSortCodes,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    Constants: ConstantsProp,
}) => {
    const ConstantsRef = ConstantsProp || Constants;
    const [file, setFile] = useState(null);
    const [prmFile, setPrmFile] = useState(null);
    const [amountDivisor, setAmountDivisor] = useState(1);
    const [reverseDescription, setReverseDescription] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [movinDatEditableMissing, setMovinDatEditableMissing] = useState([]);
    const [movinDatSelectedRows, setMovinDatSelectedRows] = useState([]);
    const [movinDatBulkVatType, setMovinDatBulkVatType] = useState(null);
    const [movinDatBulkGroup, setMovinDatBulkGroup] = useState(null);
    const [movinDatBulkPar, setMovinDatBulkPar] = useState(null);
    const [movinDatBulkVat, setMovinDatBulkVat] = useState(null);
    const [movinDatShowMissingSection, setMovinDatShowMissingSection] = useState(false);
    const [orderBy, setOrderBy] = useState('lineNumber');
    const [order, setOrder] = useState('asc');

    const handleDrop = (files) => {
        if (files?.length) setFile(files[0]);
        setPreviewData(null);
        setMovinDatEditableMissing([]);
        setMovinDatShowMissingSection(false);
    };

    const handlePrmDrop = (files) => {
        if (files?.length) setPrmFile(files[0]);
        else setPrmFile(null);
        setPreviewData(null);
        setMovinDatEditableMissing([]);
        setMovinDatShowMissingSection(false);
    };

    const normalizeMovinMissingSortCodes = useCallback((missing) => (
        (missing ?? []).map((sc) => ({
            sortCodeId: sc.sortCodeId ?? sc.SortCodeId ?? '',
            sortCodeName: sc.sortCodeName ?? sc.SortCodeName ?? '',
            sortCodeVatType: sc.sortCodeVatType ?? sc.SortCodeVatType ?? null,
            sortCodeGroup: sc.sortCodeGroup ?? sc.SortCodeGroup ?? null,
            participatesInProfitAndLoss:
                typeof sc.participatesInProfitAndLoss === 'boolean'
                    ? sc.participatesInProfitAndLoss
                    : typeof sc.ParticipatesInProfitAndLoss === 'boolean'
                      ? sc.ParticipatesInProfitAndLoss
                      : null,
            vat: typeof sc.vat === 'number' ? sc.vat : typeof sc.Vat === 'number' ? sc.Vat : null,
        }))
    ), []);

    const sortCodeIdToName = useMemo(() => {
        const m = new Map();
        (sortCodeOptions || []).forEach((o) => {
            const id = String(o.sortCodeId ?? o.value ?? '').trim();
            if (id) m.set(id, String(o.sortCodeName ?? o.name ?? o.label ?? '').trim() || id);
        });
        return m;
    }, [sortCodeOptions]);

    const cardIdToName = useMemo(() => {
        const m = new Map();
        (accountCardOptions || [])
            .filter((o) => o.value1 !== 'AddItem')
            .forEach((o) => {
                const id = o.cardId ?? o.value1 ?? o.value;
                if (id == null || id === '') return;
                const strId = String(id).trim();
                const name = String(o.name ?? o.label ?? o.accountCaseId ?? '').trim() || strId;
                m.set(strId, name);
                if (/^\d+$/.test(strId)) m.set(Number(strId), name);
            });
        return m;
    }, [accountCardOptions]);

    const fetchEnumsRef = useRef(fetchEnums);
    fetchEnumsRef.current = fetchEnums;
    useEffect(() => {
        const isEmpty = !accountCardOptions || accountCardOptions.length === 0;
        if (open && caseId && token && isEmpty && typeof fetchEnumsRef.current === 'function') {
            fetchEnumsRef.current();
        }
    }, [open, caseId, token, accountCardOptions]);

    const handlePreview = async () => {
        if (!file || !caseId || !token || !previewMovinDat) return;
        try {
            setProcessing(true);
            const res = await previewMovinDat(caseId, file, token, amountDivisor, undefined, prmFile || undefined, reverseDescription);
            const entries = res.entries ?? [];
            setPreviewData({
                entries,
                errorMessages: res.errorMessages ?? [],
                totalLines: res.totalLines ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
            });
            setPage(0);
            setMovinDatEditableMissing(normalizeMovinMissingSortCodes(res.missingSortCodes));
            setMovinDatSelectedRows([]);
            setMovinDatShowMissingSection(false);
        } catch (err) {
            console.error('Movein.dat preview failed:', err);
            setPreviewData({
                entries: [],
                errorMessages: [err.message || 'Preview failed'],
                totalLines: 0,
                validCount: 0,
                errorCount: 1,
            });
            setMovinDatEditableMissing([]);
        } finally {
            setProcessing(false);
        }
    };

    const handleCommit = async () => {
        if (!file || !caseId || !token || !commitMovinDat) return;
        try {
            setProcessing(true);
            if (createMissingSortCodes && movinDatEditableMissing.length > 0) {
                await createMissingSortCodes(movinDatEditableMissing);
                await new Promise((resolve) => setTimeout(resolve, 500));
            }
            const res = await commitMovinDat(caseId, file, token, amountDivisor, undefined, prmFile || undefined, reverseDescription);
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            const messages = res?.errorMessages ?? [];
            if (errors > 0 && messages.length > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}, ${errors} ${t('Errors') || 'errors'}.`);
                onSuccess?.();
                handleClose();
            } else if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}, ${errors} ${t('Errors') || 'errors'}.`);
                onSuccess?.();
                handleClose();
            } else {
                toast.success(processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful'));
                onSuccess?.();
                handleClose();
            }
        } catch (err) {
            console.error('Movein.dat commit failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = useCallback(() => {
        setFile(null);
        setPrmFile(null);
        setPreviewData(null);
        setPage(0);
        setRowsPerPage(10);
        setMovinDatEditableMissing([]);
        setMovinDatSelectedRows([]);
        setMovinDatShowMissingSection(false);
        onClose();
    }, [onClose]);

    const updateMovinDatMissing = useCallback((index, field, value) => {
        setMovinDatEditableMissing((prev) => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });
    }, []);

    const applyMovinDatBulk = useCallback(() => {
        setMovinDatEditableMissing((prev) =>
            prev.map((r) =>
                movinDatSelectedRows.includes(r.sortCodeId)
                    ? {
                          ...r,
                          sortCodeVatType: movinDatBulkVatType?.value ?? r.sortCodeVatType,
                          sortCodeGroup: movinDatBulkGroup?.value ?? r.sortCodeGroup,
                          participatesInProfitAndLoss: movinDatBulkPar?.value ?? r.participatesInProfitAndLoss,
                          vat: movinDatBulkVat?.value ?? r.vat,
                      }
                    : r
            )
        );
        setMovinDatSelectedRows([]);
        setMovinDatBulkVatType(null);
        setMovinDatBulkGroup(null);
        setMovinDatBulkPar(null);
        setMovinDatBulkVat(null);
    }, [movinDatSelectedRows, movinDatBulkVatType, movinDatBulkGroup, movinDatBulkPar, movinDatBulkVat]);

    const movinDatMissingIncomplete = useMemo(
        () =>
            movinDatEditableMissing.some(
                (sc) =>
                    sc.sortCodeVatType === null ||
                    sc.sortCodeVatType === undefined ||
                    sc.participatesInProfitAndLoss === null ||
                    sc.participatesInProfitAndLoss === undefined ||
                    sc.vat === null ||
                    sc.vat === undefined
            ),
        [movinDatEditableMissing]
    );

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const entries = previewData?.entries ?? [];
    const getComparator = (order, orderBy) => (a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];
        const isNum = (v) => v != null && !Number.isNaN(Number(v));
        if (order === 'asc') {
            if (isNum(aVal) && isNum(bVal)) return Number(aVal) - Number(bVal);
            return String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, { numeric: true });
        }
        if (isNum(aVal) && isNum(bVal)) return Number(bVal) - Number(aVal);
        return String(bVal ?? '').localeCompare(String(aVal ?? ''), undefined, { numeric: true });
    };
    const sortedEntries = useMemo(() => {
        if (!entries.length) return [];
        return [...entries].sort(getComparator(order, orderBy));
    }, [entries, order, orderBy]);

    const previewSummaries = useMemo(() => {
        const valid = entries.filter((e) => e.isValid);
        const sumAmount = valid.reduce((s, e) => s + (Number(e.amount) || 0), 0);
        const hasPrm = valid.some((e) => e.gross != null || e.net != null || e.vat != null);
        const sumGross = hasPrm ? valid.reduce((s, e) => s + (Number(e.gross) ?? Number(e.amount) ?? 0), 0) : null;
        const sumNet = hasPrm ? valid.reduce((s, e) => s + (Number(e.net) ?? 0), 0) : null;
        const sumVat = hasPrm ? valid.reduce((s, e) => s + (Number(e.vat) ?? 0), 0) : null;
        return { sumAmount, sumGross, sumNet, sumVat, hasPrm };
    }, [entries]);

    if (!open) return null;

    const paginatedEntries = sortedEntries.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const renderSortHeader = (id, label, align = 'left') => (
        <TableCell align={align} sx={{ fontWeight: 700, fontSize: 12 }}>
            <TableSortLabel
                active={orderBy === id}
                direction={orderBy === id ? order : 'asc'}
                onClick={() => handleRequestSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={(e) => e.stopPropagation()}>
                <div
                    className={`download-modal-content ${direction}`}
                    style={{
                        position: 'relative',
                        maxWidth: previewData ? '1100px' : '560px',
                    }}
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                    >
                        <h4 className="report-title">
                            {t('UploadMoveInFile')} – {t('Preview')} & {t('Import')}
                        </h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Step 1: File + divisor + Preview */}
                    <Box display="flex" flexDirection="column" gap={2}>
                        <div>
                            <h5>movein.dat/movein.doc</h5>
                            <DropzoneArea
                                key={file ? file.name : 'movin-empty'}
                                onDrop={handleDrop}
                                accept=".dat"
                            />
                            {file && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {t('FileSelected')}: {file.name}
                                </Typography>
                            )}
                        </div>
                        <div>
                            <h5>MOVEIN.PRM {t('Optional') || '(אופציונלי)'}</h5>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {t('MoveinPrmHelp') || 'קובץ PRM אופציונלי – מגדיר את מיקומי העמודות ב-DAT. אם לא נבחר, ייעשה שימוש בפורמט Standard/Alternate.'}
                            </Typography>
                            <DropzoneArea
                                key={prmFile ? prmFile.name : 'prm-empty'}
                                onDrop={handlePrmDrop}
                                accept=".prm"
                            />
                            {prmFile && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {t('FileSelected')}: {prmFile.name}
                                </Typography>
                            )}
                        </div>

                        <FormControl component="fieldset" sx={{ mt: 1 }}>
                            <FormLabel component="legend" sx={{ fontSize: 14, fontWeight: 600 }}>
                                {t('MoveinDatAmountDivisor')}
                            </FormLabel>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('MoveinDatAmountDivisorHelp')}
                            </Typography>
                            <RadioGroup
                                row
                                value={amountDivisor}
                                onChange={(e) => {
                                    setAmountDivisor(Number(e.target.value));
                                    setPreviewData(null);
                                }}
                            >
                                <FormControlLabel
                                    value={100}
                                    control={<Radio />}
                                    label={t('MoveinDatAmountDivisor100')}
                                />
                                <FormControlLabel
                                    value={10}
                                    control={<Radio />}
                                    label={t('MoveinDatAmountDivisor10')}
                                />
                                <FormControlLabel
                                    value={1}
                                    control={<Radio />}
                                    label={t('MoveinDatAmountDivisor1')}
                                />
                            </RadioGroup>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={reverseDescription}
                                    onChange={(e) => setReverseDescription(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={t('MoveinReverseDescription') || 'Reverse Hebrew description (description in file is stored RTL)'}
                        />

                        <Box display="flex" gap={1.5} mt={1}>
                            <Button
                                variant="contained"
                                onClick={handlePreview}
                                disabled={!file || processing}
                            >
                                {processing && !previewData ? t('Processing') : t('Preview')}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleCommit}
                                disabled={
                                    !previewData ||
                                    processing ||
                                    (previewData?.validCount ?? 0) === 0 ||
                                    (movinDatEditableMissing.length > 0 && movinDatMissingIncomplete)
                                }
                            >
                                {processing && previewData ? t('Processing') : t('Import')}
                            </Button>
                        </Box>
                    </Box>

                    {/* Missing sort codes – shown right after preview, above the table */}
                    {previewData && movinDatEditableMissing.length > 0 && (
                        <Box mt={2.5}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                    padding: 2,
                                    backgroundColor: movinDatMissingIncomplete ? 'action.hover' : 'grey.100',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: movinDatMissingIncomplete ? 'warning.main' : 'divider',
                                }}
                            >
                                <Typography variant="body2">
                                    {t('MissingSortCodes')}: <strong>{movinDatEditableMissing.length}</strong>
                                    {movinDatMissingIncomplete && (
                                        <Typography component="span" variant="body2" color="warning.dark" sx={{ ml: 1 }}>
                                            {t('FillRequiredFields')}
                                        </Typography>
                                    )}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setMovinDatShowMissingSection((prev) => !prev)}
                                    endIcon={movinDatShowMissingSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                >
                                    {movinDatShowMissingSection ? t('Hide') || 'Hide' : t('Open') || 'Open'}
                                </Button>
                            </Box>
                            <Collapse in={movinDatShowMissingSection}>
                                <Box mt={2}>
                                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                                        {t('MissingSortCodes')}
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" mb={2}>
                                        <Autocomplete
                                            options={formattedSortCodeVatType || []}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            value={movinDatBulkVatType}
                                            onChange={(e, newValue) => setMovinDatBulkVatType(newValue)}
                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder={t('SelectSortCodeVatType')} size="small" />
                                            )}
                                            PopperComponent={StyledPopper}
                                            sx={{ minWidth: 180 }}
                                        />
                                        <Autocomplete
                                            options={formattedSortCodesGroupNamesOptions || []}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            value={movinDatBulkGroup}
                                            onChange={(e, newValue) => setMovinDatBulkGroup(newValue)}
                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder={t('SelectSortCodeGroupName')} size="small" />
                                            )}
                                            PopperComponent={StyledPopper}
                                            sx={{ minWidth: 180 }}
                                        />
                                        <Autocomplete
                                            options={[
                                                { label: t('Yes'), value: true },
                                                { label: t('No'), value: false },
                                            ]}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            value={movinDatBulkPar}
                                            onChange={(e, newValue) => setMovinDatBulkPar(newValue)}
                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder={t('participatesInProfitAndLoss')} size="small" />
                                            )}
                                            PopperComponent={StyledPopper}
                                            sx={{ minWidth: 140 }}
                                        />
                                        <Autocomplete
                                            options={(ConstantsRef?.VATPercentage || []).map((v) => ({ label: `${v}%`, value: v }))}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            value={movinDatBulkVat}
                                            onChange={(e, newValue) => setMovinDatBulkVat(newValue)}
                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder={t('VAT')} size="small" />
                                            )}
                                            PopperComponent={StyledPopper}
                                            sx={{ minWidth: 100 }}
                                        />
                                        <BorderBoxButton
                                            variant="outlined"
                                            size="small"
                                            disabled={
                                                !movinDatBulkVatType &&
                                                !movinDatBulkGroup &&
                                                !movinDatBulkPar &&
                                                !movinDatBulkVat
                                            }
                                            onClick={applyMovinDatBulk}
                                        >
                                            {t('ApplyToSelected')}
                                        </BorderBoxButton>
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                        <Table size="small" stickyHeader className="sortcode-table editable">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12, width: '5%' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={
                                                                movinDatSelectedRows.length === movinDatEditableMissing.length &&
                                                                movinDatEditableMissing.length > 0
                                                            }
                                                            onChange={(e) =>
                                                                setMovinDatSelectedRows(
                                                                    e.target.checked
                                                                        ? movinDatEditableMissing.map((r) => r.sortCodeId)
                                                                        : []
                                                                )
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SortCode Id')}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SortCode Name')}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SelectSortCodeVatType')}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SelectSortCodeGroupName')}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('participatesInProfitAndLoss')}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('VAT')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {movinDatEditableMissing.map((sc, i) => (
                                                    <TableRow key={sc.sortCodeId}>
                                                        <TableCell sx={{ fontSize: 12 }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={movinDatSelectedRows.includes(sc.sortCodeId)}
                                                                onChange={(e) =>
                                                                    setMovinDatSelectedRows((prev) =>
                                                                        e.target.checked
                                                                            ? [...prev, sc.sortCodeId]
                                                                            : prev.filter((id) => id !== sc.sortCodeId)
                                                                    )
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                value={sc.sortCodeId}
                                                                variant="standard"
                                                                InputProps={{ readOnly: true, disableUnderline: true }}
                                                                inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                value={sc.sortCodeName ?? ''}
                                                                variant="standard"
                                                                InputProps={{ readOnly: true, disableUnderline: true }}
                                                                inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>
                                                            <Autocomplete
                                                                options={formattedSortCodeVatType || []}
                                                                getOptionLabel={(option) => option?.label ?? ''}
                                                                value={
                                                                    formattedSortCodeVatType.find(
                                                                        (opt) => opt.value === sc.sortCodeVatType?.toString()?.trim()
                                                                    ) || null
                                                                }
                                                                onChange={(e, newValue) => updateMovinDatMissing(i, 'sortCodeVatType', newValue?.value ?? null)}
                                                                isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        placeholder={t('SelectSortCodeVatType')}
                                                                        size="small"
                                                                        error={sc.sortCodeVatType == null}
                                                                    />
                                                                )}
                                                                PopperComponent={StyledPopper}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>
                                                            <Autocomplete
                                                                options={formattedSortCodesGroupNamesOptions || []}
                                                                getOptionLabel={(option) => option?.label ?? ''}
                                                                value={
                                                                    formattedSortCodesGroupNamesOptions.find((opt) => opt.value === sc.sortCodeGroup) || null
                                                                }
                                                                onChange={(e, newValue) => updateMovinDatMissing(i, 'sortCodeGroup', newValue?.value ?? null)}
                                                                isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} placeholder={t('SelectSortCodeGroupName')} size="small" />
                                                                )}
                                                                PopperComponent={StyledPopper}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>
                                                            <Autocomplete
                                                                options={[
                                                                    { label: t('Yes'), value: true },
                                                                    { label: t('No'), value: false },
                                                                ]}
                                                                getOptionLabel={(option) => option?.label ?? ''}
                                                                value={
                                                                    sc.participatesInProfitAndLoss === true
                                                                        ? { label: t('Yes'), value: true }
                                                                        : sc.participatesInProfitAndLoss === false
                                                                        ? { label: t('No'), value: false }
                                                                        : null
                                                                }
                                                                onChange={(e, newValue) => updateMovinDatMissing(i, 'participatesInProfitAndLoss', newValue?.value ?? null)}
                                                                isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        placeholder={t('participatesInProfitAndLoss')}
                                                                        size="small"
                                                                        error={sc.participatesInProfitAndLoss == null}
                                                                    />
                                                                )}
                                                                PopperComponent={StyledPopper}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>
                                                            <Autocomplete
                                                                options={(ConstantsRef?.VATPercentage || []).map((v) => ({ label: `${v}%`, value: v }))}
                                                                getOptionLabel={(option) => option?.label ?? ''}
                                                                value={
                                                                    (ConstantsRef?.VATPercentage || [])
                                                                        .map((v) => ({ label: `${v}%`, value: v }))
                                                                        .find((opt) => opt.value === sc.vat) || null
                                                                }
                                                                onChange={(e, newValue) => updateMovinDatMissing(i, 'vat', newValue?.value ?? null)}
                                                                isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        placeholder={t('VAT')}
                                                                        size="small"
                                                                        error={sc.vat == null}
                                                                    />
                                                                )}
                                                                PopperComponent={StyledPopper}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Collapse>
                        </Box>
                    )}

                    {previewData && movinDatEditableMissing.length === 0 && (previewData?.entries ?? []).some((row) => row.sortCode) && (
                        <Box mt={2} p={1.5} bgcolor="success.light" borderRadius={1} border="1px solid" borderColor="success.main">
                            <Typography variant="body2" color="success.dark">
                                ✓ {t('NoMissingSortCodes') || 'No missing sort codes.'}
                            </Typography>
                        </Box>
                    )}

                    {/* Step 2: Preview table + errors */}
                    {previewData && (
                        <Box mt={3} sx={{ maxHeight: 600, overflow: 'auto' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('TotalLines') || 'Total lines'}: <strong>{previewData.totalLines}</strong>
                                {' | '}
                                {t('ValidCount') || 'Valid'}: <strong>{previewData.validCount}</strong>
                                {' | '}
                                {t('ErrorCount') || 'Errors'}: <strong>{previewData.errorCount}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('SumAmount') || 'Sum Amount'}: <strong>{formatAmount(previewSummaries.sumAmount)}</strong>
                                {previewSummaries.hasPrm && previewSummaries.sumGross != null && (
                                    <> | {t('MoveinGross') || 'Gross'}: <strong>{formatAmount(previewSummaries.sumGross)}</strong></>
                                )}
                                {previewSummaries.hasPrm && previewSummaries.sumNet != null && (
                                    <> | {t('MoveinNet') || 'Net'}: <strong>{formatAmount(previewSummaries.sumNet)}</strong></>
                                )}
                                {previewSummaries.hasPrm && previewSummaries.sumVat != null && (
                                    <> | {t('MoveinVat') || 'VAT'}: <strong>{formatAmount(previewSummaries.sumVat)}</strong></>
                                )}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                                {t('MoveinImportCountNote') || 'If import creates fewer entries than preview, some rows may be duplicates (already in system), failed validation, or trailer lines (all 9s) and are listed in errors.'}
                            </Typography>

                            {previewData.errorMessages?.length > 0 && (
                                <Box
                                    mb={2}
                                    p={1.5}
                                    bgcolor="error.light"
                                    borderRadius={1}
                                    sx={{ maxHeight: 220, overflow: 'auto' }}
                                >
                                    <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                        {t('Errors') || 'Errors'} ({previewData.errorMessages.length})
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {previewData.errorMessages.map((msg, i) => (
                                            <li key={i}>
                                                <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {msg}
                                                </Typography>
                                            </li>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {entries.length > 0 ? (() => {
                                const showPrmColumns = entries.some((row) => row.gross != null || row.net != null || row.vat != null);
                                return (
                                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {renderSortHeader('lineNumber', t('Line') || 'Line')}
                                                {renderSortHeader('documentDate', t('DocumentDate') || 'Date')}
                                                {renderSortHeader('reportingMonth', t('ReportingMonth'))}
                                                {renderSortHeader('registrationMonth', t('RegistrationMonth'))}
                                                {renderSortHeader('reference', t('Reference'))}
                                                {showPrmColumns && (
                                                    renderSortHeader('sortCode', t('MoveinSortCode') || 'קוד סוג')
                                                )}
                                                {renderSortHeader('debitAccount', t('DebitAccount') || 'Debit')}
                                                {renderSortHeader('creditAccount', t('CreditAccount') || 'Credit')}
                                                {renderSortHeader('amount', t('Amount'), 'right')}
                                                {showPrmColumns && (
                                                    <>
                                                        {renderSortHeader('gross', t('MoveinGross') || 'Gross', 'right')}
                                                        {renderSortHeader('net', t('MoveinNet') || 'Net', 'right')}
                                                        {renderSortHeader('vat', t('MoveinVat') || 'VAT', 'right')}
                                                        {renderSortHeader('vatPercentage', t('MoveinVatPct') || 'VAT %', 'right')}
                                                    </>
                                                )}
                                                {renderSortHeader('description', t('Description'))}
                                                {renderSortHeader('isValid', t('Status') || 'Status')}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedEntries.map((row, i) => (
                                                <TableRow
                                                    key={row.lineNumber ?? i}
                                                    sx={{
                                                        bgcolor: row.isValid ? 'transparent' : 'action.hover',
                                                    }}
                                                >
                                                    <TableCell sx={{ fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.documentDate}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.reportingMonth ?? '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.registrationMonth ?? '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.reference}</TableCell>
                                                    {showPrmColumns && (
                                                        <TableCell sx={{ fontSize: 12 }}>
                                                            {row.sortCode != null && row.sortCode !== ''
                                                                ? (sortCodeIdToName.get(String(row.sortCode).trim())
                                                                    ? `${sortCodeIdToName.get(String(row.sortCode).trim())} (${row.sortCode})`
                                                                    : row.sortCode)
                                                                : '—'}
                                                        </TableCell>
                                                    )}
                                                    <TableCell sx={{ fontSize: 12 }}>
                                                        {row.debitAccount != null && row.debitAccount !== ''
                                                            ? (cardIdToName.get(String(row.debitAccount).trim()) ?? cardIdToName.get(Number(row.debitAccount)))
                                                                ? `${(cardIdToName.get(String(row.debitAccount).trim()) ?? cardIdToName.get(Number(row.debitAccount)))} (${row.debitAccount})`
                                                                : row.debitAccount
                                                            : (row.debitAccount ?? '—')}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>
                                                        {row.creditAccount != null && row.creditAccount !== ''
                                                            ? (cardIdToName.get(String(row.creditAccount).trim()) ?? cardIdToName.get(Number(row.creditAccount)))
                                                                ? `${(cardIdToName.get(String(row.creditAccount).trim()) ?? cardIdToName.get(Number(row.creditAccount)))} (${row.creditAccount})`
                                                                : row.creditAccount
                                                            : (row.creditAccount ?? '—')}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontSize: 12 }}>
                                                        {formatAmount(row.amount)}
                                                    </TableCell>
                                                    {showPrmColumns && (
                                                        <>
                                                            <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(row.gross)}</TableCell>
                                                            <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(row.net)}</TableCell>
                                                            <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(row.vat)}</TableCell>
                                                            <TableCell align="right" sx={{ fontSize: 12 }}>
                                                                {row.vatPercentage != null ? `${Number(row.vatPercentage)}%` : '—'}
                                                            </TableCell>
                                                        </>
                                                    )}
                                                    <TableCell sx={{ fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }} title={row.description}>
                                                        {row.description ?? ''}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        {row.isValid ? (
                                                            <Typography variant="body2" color="success.main">
                                                                {t('Valid') || 'OK'}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="body2" color="error.main" title={row.errorMessage}>
                                                                {row.errorMessage ?? ''}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        component="div"
                                        count={sortedEntries.length}
                                        page={page}
                                        onPageChange={(_, p) => setPage(p)}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                    />
                                    </TableContainer>
                                );
                            })() : null}
                        </Box>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MoveinDatImportModal;
